import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "@/components/intel/Layout";
import { ModuleShell } from "@/components/intel/ModuleShell";
import { GraphCanvas } from "@/components/intel/GraphCanvas";
import { RelationshipHeatmap } from "@/components/intel/RelationshipHeatmap";
import { IdentityResolutionPanel } from "@/components/intel/IdentityResolutionPanel";
import { Button } from "@/components/ui/button";
import {
  loadGraph, addGraphNode, deleteGraphNode, expandGraphNode,
  askGraphAssistant, listGraphChat,
  type GraphNode,
} from "@/lib/intel/graph.functions";
import { resolveIdentities } from "@/lib/intel/identity.functions";
import type { IdentityResolutionReport } from "@/lib/intel/identity-types";
import { getPaddleEnvironment } from "@/lib/paddle";
import { ArrowLeft, Plus, Loader2, Send, Network, Grid3X3 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/vault/$scanId/graph")({
  head: ({ params }) => ({ meta: [
    { title: `Relationship Heatmap · ${params.scanId.slice(0, 8)} · CARIO` },
    { name: "description", content: "Rank and investigate the strongest entity relationships within a saved CARIO case." },
    { property: "og:title", content: "Relationship Heatmap · CARIO" },
    { property: "og:description", content: "Rank and investigate the strongest entity relationships within a saved CARIO case." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: () => {
    const { scanId } = Route.useParams();
    return <AppLayout><GraphPage scanId={scanId} /></AppLayout>;
  },
  errorComponent: ({ error }) => <AppLayout><div className="p-6 text-sm text-danger">Failed: {error.message}</div></AppLayout>,
});

function GraphPage({ scanId }: { scanId: string }) {
  const load = useServerFn(loadGraph);
  const addN = useServerFn(addGraphNode);
  const delN = useServerFn(deleteGraphNode);
  const expand = useServerFn(expandGraphNode);
  const listChat = useServerFn(listGraphChat);
  const ask = useServerFn(askGraphAssistant);
  const resolve = useServerFn(resolveIdentities);
  const qc = useQueryClient();
  const [selected, setSelected] = useState<GraphNode | null>(null);
  const [view, setView] = useState<"heatmap" | "graph">("heatmap");
  const [identityReport, setIdentityReport] = useState<IdentityResolutionReport>();

  const gq = useQuery({
    queryKey: ["graph", scanId],
    queryFn: () => load({ data: { scanRunId: scanId } }),
  });

  const nodes = useMemo(() => {
    if (!gq.data) return [] as GraphNode[];
    return [...gq.data.seed.nodes, ...gq.data.nodes];
  }, [gq.data]);
  const edges = useMemo(() => {
    if (!gq.data) return [];
    return [...gq.data.seed.edges, ...gq.data.edges];
  }, [gq.data]);

  const expandM = useMutation({
    mutationFn: (n: GraphNode) => expand({ data: {
      scanRunId: scanId,
      nodeId: n.id,
      nodeLabel: n.label,
      nodeType: n.type,
      environment: getPaddleEnvironment(),
    } }),
    onSuccess: (r) => {
      toast.success(`Added ${r.added.length} related entities`);
      qc.invalidateQueries({ queryKey: ["graph", scanId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const deleteM = useMutation({
    mutationFn: (n: GraphNode) => delN({ data: { id: n.id, scanRunId: scanId } }),
    onSuccess: () => { setSelected(null); qc.invalidateQueries({ queryKey: ["graph", scanId] }); },
    onError: (e: Error) => toast.error(e.message),
  });
  const resolveM = useMutation({
    mutationFn: () => resolve({ data: { scanRunId: scanId, environment: getPaddleEnvironment() } }),
    onSuccess: (report) => {
      setIdentityReport(report);
      toast.success(report.clusters.length
        ? `Resolved ${report.clusters.length} identity ${report.clusters.length === 1 ? "cluster" : "clusters"}`
        : "No supported identity clusters found");
      qc.invalidateQueries({ queryKey: ["graph", scanId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Add node form
  const [addOpen, setAddOpen] = useState(false);
  const [nLabel, setNLabel] = useState("");
  const [nType, setNType] = useState("person");
  const [nConnect, setNConnect] = useState<string>("seed:target");
  const addM = useMutation({
    mutationFn: () => addN({ data: { scanRunId: scanId, label: nLabel.trim(), type: nType, connectTo: nConnect, source: "manual" } }),
    onSuccess: () => { setNLabel(""); setAddOpen(false); qc.invalidateQueries({ queryKey: ["graph", scanId] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <ModuleShell
      eyebrow="Entity Graph"
      title="Relationship heatmap"
      description="See the strongest entity connections first, compare confidence, and expand any relationship. All changes are saved."
      meta={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Link to="/vault/$scanId" params={{ scanId }} className="inline-flex items-center gap-2 rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs">
            <ArrowLeft className="h-3 w-3" /> Scan
          </Link>
          <div className="flex rounded-md border border-panel-border bg-panel/40 p-0.5" aria-label="Relationship view">
            <Button size="sm" variant={view === "heatmap" ? "secondary" : "ghost"} onClick={() => setView("heatmap")} className="h-7 px-2">
              <Grid3X3 /> Heatmap
            </Button>
            <Button size="sm" variant={view === "graph" ? "secondary" : "ghost"} onClick={() => setView("graph")} className="h-7 px-2">
              <Network /> Graph
            </Button>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAddOpen((v) => !v)}
          >
            <Plus /> Add node
          </Button>
        </div>
      }
    >
      {addOpen && (
        <form
          onSubmit={(e) => { e.preventDefault(); if (nLabel.trim()) addM.mutate(); }}
          className="glass rounded-xl p-3 flex flex-wrap items-end gap-2"
        >
          <div className="w-full flex-1 sm:w-auto sm:min-w-[200px]">
            <div className="font-mono text-[10px] uppercase text-muted-foreground">Label</div>
            <input value={nLabel} onChange={(e) => setNLabel(e.target.value)} placeholder="e.g. acme.io / @handle / John Doe"
              className="w-full rounded-md border border-panel-border bg-background/40 px-2 py-1.5 text-sm outline-none focus:border-cyan/40" />
          </div>
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase text-muted-foreground">Type</div>
            <select value={nType} onChange={(e) => setNType(e.target.value)}
              className="w-full rounded-md border border-panel-border bg-background/40 px-2 py-1.5 text-sm sm:w-auto">
              {["person","org","email","domain","ip","username","phone","wallet","location","device"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="w-full min-w-0 sm:w-auto sm:min-w-[180px]">
            <div className="font-mono text-[10px] uppercase text-muted-foreground">Connect to</div>
            <select value={nConnect} onChange={(e) => setNConnect(e.target.value)}
              className="w-full rounded-md border border-panel-border bg-background/40 px-2 py-1.5 text-sm">
              {nodes.map((n) => <option key={n.id} value={n.id}>{n.label.slice(0, 40)}</option>)}
            </select>
          </div>
          <button type="submit" disabled={!nLabel.trim() || addM.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50">
            {addM.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Save
          </button>
        </form>
      )}

      <IdentityResolutionPanel
        nodes={nodes}
        report={identityReport}
        isResolving={resolveM.isPending}
        onResolve={() => resolveM.mutate()}
      />

      <div className="grid gap-3 lg:grid-cols-4">
        <div className="h-[60vh] min-h-[360px] rounded-xl border border-panel-border bg-panel/20 p-2 sm:min-h-[520px] lg:col-span-3 lg:h-[70vh]">
          {gq.isLoading ? (
            <div className="grid h-full place-items-center text-sm text-muted-foreground">Loading graph…</div>
          ) : view === "heatmap" ? (
            <RelationshipHeatmap
              nodes={nodes} edges={edges}
              selectedId={selected?.id ?? null}
              onSelect={setSelected}
              onExpand={(n) => expandM.mutate(n)}
              onDelete={(n) => { if (confirm(`Remove node "${n.label}"?`)) deleteM.mutate(n); }}
              isExpanding={expandM.isPending}
            />
          ) : (
            <GraphCanvas
              nodes={nodes} edges={edges}
              selectedId={selected?.id ?? null}
              onSelect={setSelected}
              onExpand={(n) => expandM.mutate(n)}
              onDelete={(n) => { if (confirm(`Remove node "${n.label}"?`)) deleteM.mutate(n); }}
              isExpanding={expandM.isPending}
            />
          )}
        </div>
        <GraphAssistant scanId={scanId} askFn={ask} listFn={listChat} onGraphChange={() => qc.invalidateQueries({ queryKey: ["graph", scanId] })} />
      </div>
    </ModuleShell>
  );
}

function GraphAssistant({
  scanId, askFn, listFn, onGraphChange,
}: {
  scanId: string;
  askFn: (args: { data: { scanRunId: string; message: string; environment: "sandbox" | "live" } }) => Promise<{ reply: string }>;
  listFn: (args: { data: { scanRunId: string } }) => Promise<Array<{ role: "user" | "assistant"; content: string; ts: string }>>;
  onGraphChange: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const cq = useQuery({
    queryKey: ["graph-chat", scanId],
    queryFn: () => listFn({ data: { scanRunId: scanId } }),
  });
  const sendM = useMutation({
    mutationFn: (msg: string) => askFn({ data: {
      scanRunId: scanId,
      message: msg,
      environment: getPaddleEnvironment(),
    } }),
    onSuccess: () => {
      setDraft("");
      qc.invalidateQueries({ queryKey: ["graph-chat", scanId] });
      onGraphChange();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 9e9 });
  }, [cq.data, sendM.isPending]);

  return (
    <aside className="glass rounded-xl p-3 flex flex-col h-[70vh]">
      <div className="flex items-center gap-2 pb-2 border-b border-panel-border">
        <Network className="h-3.5 w-3.5 text-cyan" />
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Graph AI Assistant</div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto py-3 space-y-2">
        {cq.isLoading && <div className="text-xs text-muted-foreground">Loading…</div>}
        {!cq.isLoading && !cq.data?.length && (
          <div className="text-xs text-muted-foreground space-y-2">
            <p>Ask the AI to reason about this graph.</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>"Which nodes should I expand next?"</li>
              <li>"Are any two of these entities likely the same person?"</li>
              <li>"Suggest pivots I'm missing."</li>
            </ul>
          </div>
        )}
        {cq.data?.map((m, i) => (
          <div key={i} className={`rounded-md border border-panel-border p-2 text-xs ${m.role === "user" ? "bg-background/40" : "bg-cyan/5"}`}>
            <div className="font-mono text-[9px] uppercase text-muted-foreground">
              {m.role === "user" ? "You" : "Graph AI"} · {new Date(m.ts).toLocaleTimeString()}
            </div>
            <div className="mt-1 whitespace-pre-wrap">{m.content}</div>
          </div>
        ))}
        {sendM.isPending && (
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" /> Thinking…
          </div>
        )}
      </div>
      <form
        onSubmit={(e) => { e.preventDefault(); const m = draft.trim(); if (m) sendM.mutate(m); }}
        className="border-t border-panel-border pt-2 space-y-2"
      >
        <textarea
          value={draft} onChange={(e) => setDraft(e.target.value)} rows={2}
          placeholder="Ask the graph AI…"
          className="w-full resize-none rounded-md border border-panel-border bg-background/40 p-2 text-xs outline-none focus:border-cyan/40"
        />
        <button type="submit" disabled={!draft.trim() || sendM.isPending}
          className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50">
          {sendM.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Ask
        </button>
      </form>
    </aside>
  );
}